import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ApiServices from '../../shared/ApiServices/ApiServices';
import { V2Button, V2Card, V2Badge, V2Modal } from '../../shared/custom-ui/v2-components';
import { dashboardConfig, stepsConfig } from './dashboardConfig';
import RadiologistStats from './RadiologistStats';
import { DateRangePicker } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { KpiSkeleton } from '../../shared/custom-ui/skeleton/Dashboard/DashboardSkeleton';
import ModernPageHeader from '../WorkListTable/components/ModernPageHeader';

import {
    Calendar,
    TrendingUp,
    TrendingDown,
    Plus,
    Users,
    CalendarCheck,
    FileText,
    ArrowUpRight,
    ArrowDownRight,
    Filter,
    X,
    LayoutDashboard,
    Activity,
    ChevronRight,
    CalendarDays,
    CalendarClock,
    BarChart2,
    TrendingUp as TrendUp,
    BarChart,
    CalendarRange,
} from 'lucide-react';

const Icon = { Calendar, TrendingUp, TrendingDown, Plus, Users, CalendarCheck, FileText, ArrowUpRight, ArrowDownRight, Filter, X, LayoutDashboard, Activity, ChevronRight, CalendarDays, CalendarClock, BarChart2, BarChart, CalendarRange } as any;

function formatSelectedRange(startDate: Date, endDate: Date): string {
    const start = startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const end = endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return `${start} - ${end}`;
}

function formatDateForAPI(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}

const kpiTokens = {
    emerald: {
        // Total Patients — Clinical Blue (#045EB8)
        current: 'var(--v2-kpi-blue-current, #045EB8)',
        prev: 'var(--v2-kpi-blue-prev, #3a8be0)',
        gradCurr: 'var(--v2-kpi-blue-grad-curr, linear-gradient(180deg, #045EB8 0%, #2176c7 100%))',
        gradPrev: 'var(--v2-kpi-blue-grad-prev, linear-gradient(180deg, #3a8be0 0%, #6baee8 100%))',
        barBgCurr: 'var(--v2-kpi-blue-bar-bg-curr, rgba(4,94,184,0.08))',
        barBgPrev: 'var(--v2-kpi-blue-bar-bg-prev, rgba(58,139,224,0.06))',
        barBorder: 'var(--v2-kpi-blue-bar-border, rgba(4,94,184,0.25))',
        iconBg: 'var(--v2-kpi-blue-icon-bg, rgba(4,94,184,0.1))',
        cardTint: 'var(--v2-kpi-blue-card-tint, rgba(4,94,184,0.04))',
        divider: 'var(--v2-kpi-blue-divider, rgba(4,94,184,0.15))',
        vsColor: 'var(--v2-kpi-blue-vs, rgba(4,94,184,0.5))',
    },
    blue: {
        // Appointments — Clinical Teal (#0D9488)
        current: 'var(--v2-kpi-teal-current, #0D9488)',
        prev: 'var(--v2-kpi-teal-prev, #2bb8aa)',
        gradCurr: 'var(--v2-kpi-teal-grad-curr, linear-gradient(180deg, #0D9488 0%, #14b8a6 100%))',
        gradPrev: 'var(--v2-kpi-teal-grad-prev, linear-gradient(180deg, #2bb8aa 0%, #5eceC5 100%))',
        barBgCurr: 'var(--v2-kpi-teal-bar-bg-curr, rgba(13,148,136,0.08))',
        barBgPrev: 'var(--v2-kpi-teal-bar-bg-prev, rgba(43,184,170,0.06))',
        barBorder: 'var(--v2-kpi-teal-bar-border, rgba(13,148,136,0.25))',
        iconBg: 'var(--v2-kpi-teal-icon-bg, rgba(13,148,136,0.1))',
        cardTint: 'var(--v2-kpi-teal-card-tint, rgba(13,148,136,0.04))',
        divider: 'var(--v2-kpi-teal-divider, rgba(13,148,136,0.15))',
        vsColor: 'var(--v2-kpi-teal-vs, rgba(13,148,136,0.5))',
    },
    purple: {
        // Studies Completed — Medical Green (#16A34A)
        current: 'var(--v2-kpi-green-current, #16A34A)',
        prev: 'var(--v2-kpi-green-prev, #4abe78)',
        gradCurr: 'var(--v2-kpi-green-grad-curr, linear-gradient(180deg, #16A34A 0%, #22c55e 100%))',
        gradPrev: 'var(--v2-kpi-green-grad-prev, linear-gradient(180deg, #4abe78 0%, #7dd3a2 100%))',
        barBgCurr: 'var(--v2-kpi-green-bar-bg-curr, rgba(22,163,74,0.08))',
        barBgPrev: 'var(--v2-kpi-green-bar-bg-prev, rgba(74,190,120,0.06))',
        barBorder: 'var(--v2-kpi-green-bar-border, rgba(22,163,74,0.25))',
        iconBg: 'var(--v2-kpi-green-icon-bg, rgba(22,163,74,0.1))',
        cardTint: 'var(--v2-kpi-green-card-tint, rgba(22,163,74,0.04))',
        divider: 'var(--v2-kpi-green-divider, rgba(22,163,74,0.15))',
        vsColor: 'var(--v2-kpi-green-vs, rgba(22,163,74,0.5))',
    },
} as const;

const EnhancedComparisonChart = ({
    prev,
    curr,
    prevLabel,
    currLabel,
    color = 'emerald',
}: {
    prev: number;
    curr: number;
    prevLabel: string;
    currLabel: string;
    color?: keyof typeof kpiTokens;
}) => {
    const max = Math.max(prev, curr, 1);
    const prevHeight = (prev / max) * 100;
    const currHeight = (curr / max) * 100;
    const ct = kpiTokens[color];

    return (
        <div className="mt-2">
            <div className="grid grid-cols-2 gap-3 px-0.5">
                {/* Current */}
                <div className="flex flex-col items-center">
                    <div className="text-center mb-2">
                        <div
                            className="text-[10px] font-bold tracking-[0.12em] uppercase mb-1"
                            style={{ color: ct.current }}
                        >
                            Current
                        </div>
                        <div
                            className="text-[30px] font-black leading-none mb-1 tracking-[-0.02em]"
                            style={{ color: ct.current }}
                        >
                            {curr}
                        </div>
                        <div
                            className="text-[11px] font-medium max-w-[120px] overflow-hidden text-ellipsis whitespace-nowrap"
                            style={{ color: ct.current, opacity: 0.65 }}
                        >
                            {currLabel}
                        </div>
                    </div>
                    {/* Bar */}
                    <div
                        className="relative w-11 h-12 rounded-[var(--v2-radius-sm)] overflow-hidden"
                        style={{
                            background: ct.barBgCurr,
                            border: `1.5px solid ${ct.barBorder}`,
                        }}
                        title={`${currLabel}: ${curr}`}
                    >
                        <div
                            className="absolute bottom-0 w-full rounded-[2px_2px_0_0] transition-[height] duration-[700ms] ease-[cubic-bezier(0.25,0.1,0.25,1)]"
                            style={{ background: ct.gradCurr, height: `${Math.max(currHeight, 8)}%` }}
                        >
                            <div className="absolute inset-0 bg-gradient-to-b from-white/20 to-transparent" />
                        </div>
                    </div>
                </div>

                {/* Previous */}
                <div className="flex flex-col items-center">
                    <div className="text-center mb-2">
                        <div
                            className="text-[10px] font-bold tracking-[0.12em] uppercase mb-1"
                            style={{ color: ct.prev }}
                        >
                            Previous
                        </div>
                        <div
                            className="text-[30px] font-black leading-none mb-1 tracking-[-0.02em]"
                            style={{ color: ct.prev }}
                        >
                            {prev}
                        </div>
                        <div
                            className="text-[11px] font-medium max-w-[120px] overflow-hidden text-ellipsis whitespace-nowrap"
                            style={{ color: ct.prev, opacity: 0.65 }}
                        >
                            {prevLabel}
                        </div>
                    </div>
                    {/* Bar */}
                    <div
                        className="relative w-10 h-12 rounded-[var(--v2-radius-sm)] overflow-hidden"
                        style={{
                            background: ct.barBgPrev,
                            border: `1.5px solid ${ct.barBorder}`,
                            opacity: 0.72,
                        }}
                        title={`${prevLabel}: ${prev}`}
                    >
                        <div
                            className="absolute bottom-0 w-full rounded-[2px_2px_0_0] transition-[height] duration-[700ms] ease-[cubic-bezier(0.25,0.1,0.25,1)]"
                            style={{ background: ct.gradPrev, height: `${Math.max(prevHeight, 8)}%` }}
                        >
                            <div className="absolute inset-0 bg-gradient-to-b from-white/15 to-transparent" />
                        </div>
                    </div>
                </div>
            </div>

            {/* Legend */}
            <div
                className="mt-2 pt-2 flex items-center justify-center gap-2.5"
                style={{ borderTop: `1px solid ${ct.divider}` }}
            >
                <span
                    className="inline-flex items-center gap-1 text-[12px] font-semibold"
                    style={{ color: ct.current }}
                >
                    <span className="w-1.5 h-1.5 rounded-full shrink-0 inline-block" style={{ background: ct.current }} />
                    Current
                </span>
                <span className="text-[11px] font-medium" style={{ color: ct.vsColor }}>vs</span>
                <span
                    className="inline-flex items-center gap-1 text-[12px] font-semibold"
                    style={{ color: ct.prev }}
                >
                    <span className="w-1.5 h-1.5 rounded-full shrink-0 inline-block" style={{ background: ct.prev }} />
                    Previous
                </span>
            </div>
        </div>
    );
};

const DeltaBadge = ({ value }: { value: number | null }) => {
    if (value === null || value === undefined) {
        return <V2Badge variant="default" size="small">No change</V2Badge>;
    }
    if (value === 0) {
        return <V2Badge variant="default" size="small">0%</V2Badge>;
    }

    const up = value >= 0;
    return (
        <V2Badge variant={up ? 'success' : 'danger'} size="small" dot>
            <span className="inline-flex items-center gap-1">
                {up ? <Icon.ArrowUpRight style={{ width: 14, height: 14 }} strokeWidth={2.5} /> : <Icon.ArrowDownRight style={{ width: 14, height: 14 }} strokeWidth={2.5} />}
                {up && value > 0 ? '+' : ''}{value}%
            </span>
        </V2Badge>
    );
};

const iconBgMap = {
    emerald: { bg: 'var(--v2-kpi-blue-icon-bg)', color: 'var(--v2-kpi-blue-current)' },
    blue: { bg: 'var(--v2-kpi-teal-icon-bg)', color: 'var(--v2-kpi-teal-current)' },
    purple: { bg: 'var(--v2-kpi-green-icon-bg)', color: 'var(--v2-kpi-green-current)' },
} as const;

const StatCardV2 = ({
    title,
    icon: IconComponent,
    current,
    previous,
    growth,
    currentLabel,
    previousLabel,
    color,
    loading,
}: {
    title: string;
    icon: any;
    current: number;
    previous: number;
    growth: number | null;
    currentLabel: string;
    previousLabel: string;
    color: 'emerald' | 'blue' | 'purple';
    loading: boolean;
}) => {
    const ic = iconBgMap[color];

    if (loading) {
        return (
            <div className="rounded-[var(--v2-radius-lg)] bg-[var(--v2-bg-card-inner)] border border-[var(--v2-border-glass-strong)] shadow-[var(--v2-shadow-card)] p-3">
                <KpiSkeleton />
            </div>
        );
    }

    return (
        <div
            className="relative rounded-[var(--v2-radius-lg)] border border-[var(--v2-border-glass-strong)] shadow-[var(--v2-shadow-card)] p-3 overflow-hidden transition-[box-shadow,border-color] duration-[var(--v2-duration-normal)]"
            style={{ background: `linear-gradient(160deg, ${kpiTokens[color].cardTint} 0%, var(--v2-bg-card-inner) 40%)` }}
            onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.boxShadow = 'var(--v2-shadow-card-hover)';
                (e.currentTarget as HTMLElement).style.borderColor = 'var(--v2-kpi-card-hover-border)';
            }}
            onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.boxShadow = 'var(--v2-shadow-card)';
                (e.currentTarget as HTMLElement).style.borderColor = 'var(--v2-border-glass-strong)';
            }}
        >
            {/* Accent top border — theme-aware gradient */}
            <div className="absolute inset-x-0 top-0 h-[3px]" style={{ background: kpiTokens[color].gradCurr }} />

            {/* Header */}
            <div className="flex items-center gap-2 mb-2 mt-[3px]">
                <div
                    className="p-2 rounded-[var(--v2-radius-sm)] flex items-center justify-center shrink-0"
                    style={{ background: ic.bg, color: ic.color, border: `1px solid ${kpiTokens[color].barBorder}` }}
                >
                    <IconComponent style={{ width: 16, height: 16 }} strokeWidth={2} />
                </div>
                <div className="flex items-start justify-between gap-2 flex-1 min-w-0">
                    <h3 className="text-[12px] font-bold tracking-[0.05em] uppercase leading-none pt-[2px] m-0"
                        style={{ color: 'var(--v2-text-secondary)' }}>
                        {title}
                    </h3>
                    <div className="shrink-0">
                        <DeltaBadge value={growth} />
                    </div>
                </div>
            </div>

            {/* Chart */}
            <EnhancedComparisonChart
                prev={previous}
                curr={current}
                prevLabel={previousLabel}
                currLabel={currentLabel}
                color={color}
            />
        </div>
    );
};

interface DateRangeModalV2Props {
    dateRange: { selection: { startDate: Date; endDate: Date; key: string } };
    onChange: (range: any) => void;
    onApply: () => void;
    onClose: () => void;
}

const DateRangeModalV2: React.FC<DateRangeModalV2Props> = ({ dateRange, onChange, onApply, onClose }) => {
    const calculateComparisonPeriod = () => {
        const currentStart = dateRange.selection.startDate;
        const currentEnd = dateRange.selection.endDate;
        const durationInDays = Math.ceil((currentEnd.getTime() - currentStart.getTime()) / (1000 * 60 * 60 * 24));
        const prevEnd = new Date(currentStart);
        prevEnd.setDate(prevEnd.getDate() - 1);
        const prevStart = new Date(prevEnd);
        prevStart.setDate(prevStart.getDate() - durationInDays);
        return { prevStart, prevEnd, durationInDays };
    };

    const { durationInDays } = calculateComparisonPeriod();

    const getActivePreset = () => {
        const start = dateRange.selection.startDate;
        const end = dateRange.selection.endDate;
        const now = new Date();

        const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        const thisMonthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        if (start.getTime() === thisMonthStart.getTime() && end.getTime() === thisMonthEnd.getTime()) return 'thisMonth';

        const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);
        if (start.getTime() === lastMonthStart.getTime() && end.getTime() === lastMonthEnd.getTime()) return 'lastMonth';

        const last7End = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const last7Start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6);
        if (formatDateForAPI(start) === formatDateForAPI(last7Start) && formatDateForAPI(end) === formatDateForAPI(last7End)) return 'last7days';

        const last30End = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const last30Start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 29);
        if (formatDateForAPI(start) === formatDateForAPI(last30Start) && formatDateForAPI(end) === formatDateForAPI(last30End)) return 'last30days';

        const last90End = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const last90Start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 89);
        if (formatDateForAPI(start) === formatDateForAPI(last90Start) && formatDateForAPI(end) === formatDateForAPI(last90End)) return 'last90days';

        const thisYearStart = new Date(now.getFullYear(), 0, 1);
        const thisYearEnd = new Date(now.getFullYear(), 11, 31);
        if (start.getTime() === thisYearStart.getTime() && end.getTime() === thisYearEnd.getTime()) return 'thisYear';

        return null;
    };

    const activePreset = getActivePreset();

    const handlePreset = (preset: string) => {
        const now = new Date();
        let startDate: Date, endDate: Date;
        switch (preset) {
            case 'thisMonth':
                startDate = new Date(now.getFullYear(), now.getMonth(), 1);
                endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
                break;
            case 'lastMonth':
                startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
                endDate = new Date(now.getFullYear(), now.getMonth(), 0);
                break;
            case 'last7days':
                endDate = new Date(now);
                startDate = new Date(now);
                startDate.setDate(startDate.getDate() - 6);
                break;
            case 'last30days':
                endDate = new Date(now);
                startDate = new Date(now);
                startDate.setDate(startDate.getDate() - 29);
                break;
            case 'last90days':
                endDate = new Date(now);
                startDate = new Date(now);
                startDate.setDate(startDate.getDate() - 89);
                break;
            case 'thisYear':
                startDate = new Date(now.getFullYear(), 0, 1);
                endDate = new Date(now.getFullYear(), 11, 31);
                break;
            default:
                return;
        }
        onChange({ selection: { startDate, endDate, key: 'selection' } });
    };

    const presets = [
        { label: 'This Month', value: 'thisMonth', icon: 'CalendarDays', description: 'Current month' },
        { label: 'Last Month', value: 'lastMonth', icon: 'CalendarClock', description: 'Previous month' },
        { label: 'Last 7 Days', value: 'last7days', icon: 'BarChart2', description: 'Recent week' },
        { label: 'Last 30 Days', value: 'last30days', icon: 'BarChart', description: 'Recent month' },
        { label: 'Last 90 Days', value: 'last90days', icon: 'TrendingUp', description: 'Recent quarter' },
        { label: 'This Year', value: 'thisYear', icon: 'CalendarRange', description: 'Full year' },
    ];

    return (
        <V2Modal isOpen onClose={onClose} title="Select Analytics Period" size="full" showCloseButton>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                    {/* Left — Quick Presets */}
                    <div
                        style={{
                            width: 256,
                            flexShrink: 0,
                            padding: 'var(--v2-space-5)',
                            borderRight: '1px solid var(--v2-border-subtle)',
                            background: 'var(--v2-bg-surface)',
                        }}
                    >
                        <h3 style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '0.08em', marginBottom: 14, color: 'var(--v2-text-muted)' }}>
                            Quick Select
                        </h3>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {presets.map((preset) => {
                                const isActive = activePreset === preset.value;
                                return (
                                    <button
                                        key={preset.value}
                                        type="button"
                                        onClick={() => handlePreset(preset.value)}
                                        style={{
                                            width: '100%',
                                            textAlign: 'left',
                                            padding: '10px 14px',
                                            fontSize: 13.5,
                                            fontWeight: 500,
                                            borderRadius: 'var(--v2-radius-md)',
                                            border: isActive ? '1px solid var(--v2-primary-600)' : '1px solid transparent',
                                            background: isActive ? 'var(--v2-primary-600)' : 'transparent',
                                            color: isActive ? '#fff' : 'var(--v2-text-secondary)',
                                            cursor: 'pointer',
                                            transition: `all var(--v2-duration-fast) var(--v2-ease-calm)`,
                                        }}
                                        onMouseEnter={(e) => { if (!isActive) (e.currentTarget as HTMLElement).style.background = 'var(--v2-bg-hover)'; }}
                                        onMouseLeave={(e) => { if (!isActive) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            {(() => { const PresetIcon = Icon[preset.icon]; return PresetIcon ? <PresetIcon style={{ width: 16, height: 16, flexShrink: 0, opacity: 0.8 }} /> : null; })()}
                                            <div style={{ flex: 1 }}>
                                                <div style={{ fontWeight: 600, fontSize: 13.5, color: isActive ? '#fff' : 'var(--v2-text-primary)', display: 'flex', alignItems: 'center', gap: 8 }}>
                                                    {preset.label}
                                                    {isActive && <span style={{ fontSize: 14 }}>✓</span>}
                                                </div>
                                                <div style={{ fontSize: 12, fontWeight: 400, color: isActive ? 'rgba(255,255,255,0.7)' : 'var(--v2-text-muted)', marginTop: 2 }}>
                                                    {preset.description}
                                                </div>
                                            </div>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Center — Calendar */}
                    <div style={{ flex: 1, padding: 'var(--v2-space-6)' }}>
                        {/* @ts-ignore */}
                        <DateRangePicker
                            ranges={[dateRange.selection]}
                            onChange={(item: any) => onChange({ selection: item.selection })}
                            months={2}
                            minDate={new Date(2024, 0, 1)}
                            maxDate={new Date()}
                            direction="horizontal"
                            moveRangeOnFirstSelection={false}
                            rangeColors={['#045eb8']}
                            staticRanges={[]}
                            inputRanges={[]}
                            showDateDisplay
                        />
                    </div>
                </div>
            </div>

            {/* Footer via V2Modal footer prop would be better, but since we wrap content directly: */}
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 12,
                    paddingTop: 'var(--v2-space-4)',
                    borderTop: '1px solid var(--v2-border-subtle)',
                    marginTop: 'var(--v2-space-4)',
                }}
            >
                <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--v2-text-muted)' }}>
                    <span style={{ fontWeight: 700, color: 'var(--v2-text-secondary)' }}>Tip: </span>
                    Use presets for common ranges, or select custom dates on the calendar
                </div>
                <div style={{ display: 'flex', gap: 10, flexShrink: 0 }}>
                    <V2Button variant="outline" size="small" onClick={onClose}>Cancel</V2Button>
                    <V2Button
                        variant="primary"
                        size="small"
                        onClick={onApply}
                        leftIcon={<Icon.Calendar style={{ width: 14, height: 14 }} />}
                    >
                        Apply &amp; View Analytics
                    </V2Button>
                </div>
            </div>

            {/* Theme-aware calendar styles */}
            <style>{`
                .rdrDefinedRangesWrapper { display: none !important; }
                .rdrDateRangeWrapper { width: 100% !important; }
                .rdrCalendarWrapper { background: transparent !important; color: var(--v2-text-primary) !important; }
                .rdrDateDisplayWrapper { background: var(--v2-bg-hover) !important; padding: 12px 16px; border-radius: 8px; margin-bottom: 16px; border: 1px solid var(--v2-border-card) !important; }
                .rdrDateDisplay { margin: 0; }
                .rdrDateInput { border: 1px solid var(--v2-border-card) !important; border-radius: 6px !important; background: var(--v2-bg-surface) !important; }
                .rdrDateInput input { color: var(--v2-text-primary) !important; font-weight: 500 !important; }
                .rdrMonthAndYearWrapper { padding: 12px 0; }
                .rdrMonthAndYearPickers select { color: var(--v2-text-primary) !important; }
                .rdrMonthPicker select, .rdrYearPicker select { background: var(--v2-bg-surface) !important; border: 1px solid var(--v2-border-card) !important; border-radius: 6px !important; padding: 6px 12px !important; font-weight: 500 !important; color: var(--v2-text-primary) !important; }
                .rdrDayNumber span { color: var(--v2-text-primary) !important; }
                .rdrDayToday .rdrDayNumber span:after { background: #045eb8 !important; }
                .rdrDay:not(.rdrDayPassive) .rdrInRange, .rdrDay:not(.rdrDayPassive) .rdrStartEdge, .rdrDay:not(.rdrDayPassive) .rdrEndEdge, .rdrDay:not(.rdrDayPassive) .rdrSelected { background: linear-gradient(135deg, #045eb8 0%, #3a8be0 100%) !important; }
                .rdrDay:not(.rdrDayPassive) .rdrInRange ~ .rdrDayNumber span, .rdrDay:not(.rdrDayPassive) .rdrStartEdge ~ .rdrDayNumber span, .rdrDay:not(.rdrDayPassive) .rdrEndEdge ~ .rdrDayNumber span { color: #fff !important; }
                .rdrDayPassive .rdrDayNumber span { opacity: 0.35 !important; }
                .rdrMonthAndYearWrapper button { color: var(--v2-text-secondary) !important; }
                .rdrWeekDay { color: var(--v2-text-muted) !important; }
                .rdrMonths { gap: 16px; }
                .rdrMonth { padding: 8px; }
                .rdrDayStartPreview, .rdrDayInPreview, .rdrDayEndPreview { border-color: #045eb8 !important; }
            `}</style>
        </V2Modal>
    );
};

const Dashboard: React.FC = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [analytics, setAnalytics] = useState<any>(null);

    const getCurrentMonthRange = () => {
        const now = new Date();
        const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
        const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        return { startDate: firstDay, endDate: lastDay };
    };

    const currentMonth = getCurrentMonthRange();
    const [dateRange, setDateRange] = useState({
        selection: { startDate: currentMonth.startDate, endDate: currentMonth.endDate, key: 'selection' },
    });
    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
    const [tempDateRange, setTempDateRange] = useState(dateRange);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                setLoading(true);
                const currentStart = formatDateForAPI(dateRange.selection.startDate);
                const currentEnd = formatDateForAPI(dateRange.selection.endDate);

                const currentStartDate = dateRange.selection.startDate;
                const currentEndDate = dateRange.selection.endDate;
                const durationInDays = Math.ceil((currentEndDate.getTime() - currentStartDate.getTime()) / (1000 * 60 * 60 * 24));

                const prevEndDate = new Date(currentStartDate);
                prevEndDate.setDate(prevEndDate.getDate() - 1);
                const prevStartDate = new Date(prevEndDate);
                prevStartDate.setDate(prevStartDate.getDate() - durationInDays);

                const prevStart = formatDateForAPI(prevStartDate);
                const prevEnd = formatDateForAPI(prevEndDate);

                const res = await ApiServices.getAnalyticsData(currentStart, currentEnd, prevStart, prevEnd);
                if (!alive) return;
                setAnalytics(res);
                setLoading(false);
            } catch {
                if (!alive) return;
                setAnalytics(null);
                setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [dateRange]);

    const handleApplyDateRange = () => {
        setDateRange(tempDateRange);
        setIsDatePickerOpen(false);
    };

    const handleCloseDatePicker = () => {
        setTempDateRange(dateRange);
        setIsDatePickerOpen(false);
    };

    const current = analytics?.current;
    const previous = analytics?.previous;
    const growth = analytics?.growth;

    const currentLabel = current?.start && current?.end
        ? `${new Date(current.start).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${new Date(current.end).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
        : formatSelectedRange(dateRange.selection.startDate, dateRange.selection.endDate);

    const previousLabel = previous?.start && previous?.end
        ? `${new Date(previous.start).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${new Date(previous.end).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
        : 'Previous Period';

    return (
        <>
            {/* ── Dashboard-scoped keyframes ── */}
            <style>{`
                @keyframes dash-live-pulse {
                    0%, 100% { box-shadow: 0 0 0 0 var(--v2-kpi-green-bar-border, rgba(22,163,74,0.55)); }
                    50% { box-shadow: 0 0 0 4px transparent; }
                }
                .dash-live-dot { animation: dash-live-pulse 2.2s ease infinite; }
            `}</style>
            <div className="flex flex-col gap-2.5">
                {/* ══════ HEADER SECTION ══════ */}
                <ModernPageHeader
                    title="Dashboard Overview"
                    subtitle="Real-time metrics and analytics for patient care"
                    actions={
                        <>
                            <button
                                type="button"
                                className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-[var(--v2-text-secondary)] bg-[var(--v2-bg-elevated)] border border-[var(--v2-border-card)] rounded-xl px-3 py-1.5 cursor-pointer hover:bg-[var(--v2-bg-hover)] transition-colors duration-[var(--v2-duration-fast)]"
                                onClick={() => { setTempDateRange(dateRange); setIsDatePickerOpen(true); }}
                            >
                                <Icon.Filter className="w-3 h-3 opacity-60 shrink-0" />
                                {currentLabel}
                            </button>
                            <V2Button
                                variant="secondary"
                                size="small"
                                onClick={() => navigate('/scheduling')}
                                leftIcon={<Icon.Calendar style={{ width: 14, height: 14 }} />}
                            >
                                Schedule
                            </V2Button>
                            <V2Button
                                variant="primary"
                                size="small"
                                onClick={() => navigate('/patients-history/patient-register')}
                                leftIcon={<Icon.Plus style={{ width: 14, height: 14 }} />}
                            >
                                Add Patient
                            </V2Button>
                        </>
                    }
                />

                {/* ══════ ANALYTICS CARDS ══════ */}
                <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
                    <StatCardV2
                        title="Total Patients"
                        icon={Icon.Users}
                        current={current?.total_patients ?? 0}
                        previous={previous?.total_patients ?? 0}
                        growth={growth?.patients_growth ?? null}
                        currentLabel={currentLabel}
                        previousLabel={previousLabel}
                        color="emerald"
                        loading={loading}
                    />
                    <StatCardV2
                        title="Appointments"
                        icon={Icon.CalendarCheck}
                        current={current?.total_appointments ?? 0}
                        previous={previous?.total_appointments ?? 0}
                        growth={growth?.appointments_growth ?? null}
                        currentLabel={currentLabel}
                        previousLabel={previousLabel}
                        color="blue"
                        loading={loading}
                    />
                    <StatCardV2
                        title="Studies Completed"
                        icon={Icon.FileText}
                        current={current?.total_studys ?? 0}
                        previous={previous?.total_studys ?? 0}
                        growth={growth?.studys_growth ?? null}
                        currentLabel={currentLabel}
                        previousLabel={previousLabel}
                        color="purple"
                        loading={loading}
                    />
                </div>

                {/* ══════ RADIOLOGIST STATS ══════ */}
                <RadiologistStats />

                {/* ══════ WORKFLOW STEPS ══════ */}
                <section aria-labelledby="v2-workflow-title" className="flex flex-col gap-2.5">
                    <div className="flex items-center justify-between">
                        <h2 id="v2-workflow-title" className="text-[16px] font-bold tracking-[-0.02em] text-[var(--v2-text-heading)]">
                            Workflow Steps
                        </h2>
                        <div className="text-[13px] font-medium text-[var(--v2-text-muted)]">
                            Follow these steps for optimal care
                        </div>
                    </div>

                    <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}>
                        {stepsConfig.map((item, idx) => (
                            <V2Card key={idx} hoverable noPadding>
                                <div className="p-3.5">
                                    <div className="flex items-center gap-2 mb-2.5">
                                        <div
                                            className="w-7 h-7 rounded-[var(--v2-radius-sm)] flex items-center justify-center font-extrabold text-[12px] shrink-0"
                                            style={{
                                                background: 'var(--v2-primary-50)',
                                                color: 'var(--v2-primary-600)',
                                                border: '1px solid var(--v2-border-brand)',
                                            }}
                                        >
                                            {idx + 1}
                                        </div>
                                        <div className="h-px flex-1 bg-gradient-to-r from-[var(--v2-border-card)] to-transparent" />
                                    </div>

                                    <div className="relative w-full aspect-[2.2/1] rounded-[var(--v2-radius-sm)] overflow-hidden bg-[var(--v2-bg-inset)] border border-[var(--v2-border-card)]">
                                        <img
                                            src={item.imageSrc as string}
                                            alt={item.title}
                                            className="absolute inset-0 w-full h-full object-contain p-2"
                                            loading="lazy"
                                        />
                                    </div>

                                    <h3 className="mt-2.5 text-[14.5px] font-bold tracking-[-0.01em] text-[var(--v2-text-heading)] leading-[1.35]">
                                        {item.title}
                                    </h3>
                                    <p className="mt-1 text-[13.5px] font-normal leading-[1.6] text-[var(--v2-text-secondary)]">
                                        {item.description}
                                    </p>
                                </div>
                            </V2Card>
                        ))}
                    </div>
                </section>

                {/* ══════ FEATURES & ANNOUNCEMENTS ══════ */}
                <section aria-labelledby="v2-features-title" className="flex flex-col gap-2.5">
                    <div className="flex items-center justify-between">
                        <h2 id="v2-features-title" className="text-[16px] font-bold tracking-[-0.02em] text-[var(--v2-text-heading)]">
                            Features &amp; Announcements
                        </h2>
                        <div className="text-[13px] font-medium text-[var(--v2-text-muted)]">
                            Comprehensive healthcare solutions
                        </div>
                    </div>

                    <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}>
                        {dashboardConfig.slice(0, 3).map((item, idx) => (
                            <V2Card key={idx} hoverable noPadding>
                                <div className="relative w-full aspect-[2.2/1] overflow-hidden bg-[var(--v2-bg-inset)] border border-[var(--v2-border-card)] border-b-0 rounded-t-[var(--v2-radius-sm)]">
                                    {(idx === 0 || idx === 1) && (
                                        <div className="absolute left-2 top-2 z-10">
                                            <V2Badge variant={idx === 0 ? 'info' : 'primary'} size="small">
                                                {idx === 0 ? '+ New' : 'Updated'}
                                            </V2Badge>
                                        </div>
                                    )}
                                    <img
                                        src={item.imageSrc as string}
                                        alt={item.title}
                                        className="absolute inset-0 w-full h-full object-contain p-2"
                                        loading="lazy"
                                    />
                                </div>
                                <div className="p-3.5">
                                    <h3 className="text-[14.5px] font-bold tracking-[-0.01em] text-[var(--v2-text-heading)] leading-[1.35]">
                                        {item.title}
                                    </h3>
                                    <p className="mt-1 text-[13.5px] font-normal leading-[1.6] text-[var(--v2-text-secondary)]">
                                        {item.description}
                                    </p>
                                </div>
                            </V2Card>
                        ))}
                    </div>
                </section>
            </div>

            {/* Date Range Modal */}
            {isDatePickerOpen && (
                <DateRangeModalV2
                    dateRange={tempDateRange}
                    onChange={setTempDateRange}
                    onApply={handleApplyDateRange}
                    onClose={handleCloseDatePicker}
                />
            )}
        </>
    );
};

export default Dashboard;
